import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { agentService } from '../../services/agentService';

// Async thunks
export const fetchExecutionStatus = createAsyncThunk(
  'agent/fetchExecutionStatus',
  async (executionId, { rejectWithValue }) => {
    try {
      const response = await agentService.getExecutionStatus(executionId);
      return { executionId, data: response };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchActiveExecutions = createAsyncThunk(
  'agent/fetchActiveExecutions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await agentService.getActiveExecutions();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const approveAction = createAsyncThunk(
  'agent/approveAction',
  async (actionId, { rejectWithValue }) => {
    try {
      const response = await agentService.approveAction(actionId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const rejectAction = createAsyncThunk(
  'agent/rejectAction',
  async ({ actionId, reason }, { rejectWithValue }) => {
    try {
      const response = await agentService.rejectAction(actionId, reason);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const provideMissingInfo = createAsyncThunk(
  'agent/provideMissingInfo',
  async ({ actionId, answers }, { rejectWithValue }) => {
    try {
      const response = await agentService.provideMissingInfo(actionId, answers);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const agentSlice = createSlice({
  name: 'agent',
  initialState: {
    executions: {}, // Keyed by execution_id
    activeExecutions: [], // Array of execution objects
    selectedExecution: null,
    pollingEnabled: true,
    pollingInterval: 5000, // 5 seconds
    loading: false,
    error: null
  },
  reducers: {
    updateExecution: (state, action) => {
      const { executionId, status } = action.payload;
      state.executions[executionId] = status;
    },
    setSelectedExecution: (state, action) => {
      state.selectedExecution = action.payload;
    },
    togglePolling: (state) => {
      state.pollingEnabled = !state.pollingEnabled;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch execution status
      .addCase(fetchExecutionStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchExecutionStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { executionId, data } = action.payload;
        state.executions[executionId] = data;
      })
      .addCase(fetchExecutionStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch active executions
      .addCase(fetchActiveExecutions.fulfilled, (state, action) => {
        state.activeExecutions = action.payload;
        // Also update executions object
        action.payload.forEach(execution => {
          state.executions[execution.id] = execution;
        });
      })
      // Approve action
      .addCase(approveAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(approveAction.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(approveAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Reject action
      .addCase(rejectAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(rejectAction.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(rejectAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Provide missing info
      .addCase(provideMissingInfo.pending, (state) => {
        state.loading = true;
      })
      .addCase(provideMissingInfo.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(provideMissingInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { updateExecution, setSelectedExecution, togglePolling, clearError } = agentSlice.actions;
export default agentSlice.reducer;
